'use client';
import { autoPlacement as autoPlacementMiddleware } from '@floating-ui/dom';
import {
    arrow,
    flip,
    FloatingArrow,
    FloatingFocusManager,
    FloatingPortal,
    offset,
    safePolygon,
    shift,
    size as floatingSize,
    useClick,
    useDismiss,
    useFloating,
    useFocus,
    useHover,
    useInteractions,
    useTransitionStyles
} from '@floating-ui/react';

import { ComputedPlacement } from '@popperjs/core';
import { cva, VariantProps } from 'class-variance-authority';
import React, { forwardRef, useImperativeHandle, useState } from 'react';

const tooltipClasses = cva('', {
    variants: {
        withoutStyling: {
            false: ''
        },
        flat: {
            true: '',
            false: ''
        },
        keepClosed: {
            true: 'tw-pointer-events-none',
            false: ''
        }
    },
    compoundVariants: [
        {
            className:
                'tw-z-[9999999] tw-border tw-border-accents-0 focus:tw-outline-none tw-max-h-[80vh] md:tw-max-h-[50vh] tw-rounded-2xl tw-border-solid tw-bg-white tw-px-4 tw-py-1 tw-text-gray-700 tw-shadow-lg tw-overflow-hidden',
            flat: false,
            withoutStyling: false
        },
        {
            className:
                'tw-z-[9999999] focus:tw-outline-none tw-max-h-[80vh] md:tw-max-h-[50vh] tw-rounded-xl tw-border-accents-0 tw-border-solid tw-border tw-bg-white tw-px-4 tw-py-1 tw-text-accents-8 tw-overflow-hidden tw-font-semibold',
            flat: true,
            withoutStyling: false
        }
    ],
    defaultVariants: {
        withoutStyling: false,
        flat: false
    }
});

interface Props extends React.HTMLAttributes<HTMLDivElement>, Omit<VariantProps<typeof tooltipClasses>, 'keepClosed'> {
    tooltip?: React.ReactNode;
    offset?: number;
    placement?: ComputedPlacement;
    reachable?: boolean;
    withArrow?: boolean;
    tooltipClassName?: string;
    /**
     * 'manual' leaves opening entirely to the `show` prop and keeps focus where it is —
     * required for typeahead panels, where the click mode's focus manager would pull the
     * caret out of the input. Dismissing (escape, outside click) still works.
     */
    trigger?: 'click' | 'hover' | 'manual';
    disabled?: boolean;
    autoPlacement?: boolean;
    onShow?: (show: boolean) => void;
    show?: boolean;
    /** Grows the panel to at least the width of the element it is anchored to. */
    matchReferenceWidth?: boolean;
}

export interface TooltipRef {
    show: () => void;
    hide: () => void;
}

const Tooltip = forwardRef<TooltipRef, Props>(
    (
        {
            children,
            flat,
            tooltip,
            className,
            show,
            tooltipClassName,
            offset: _offset = 5,
            placement,
            withoutStyling,
            withArrow,
            trigger = 'hover',
            disabled,
            autoPlacement,
            reachable = true,
            onShow,
            matchReferenceWidth,
            ...props
        },
        ref
    ) => {
        const arrowRef = React.useRef<SVGSVGElement>(null);
        const [isOpen, _setIsOpen] = useState(false);
        const setIsOpen = (nextOpen: boolean) => {
            _setIsOpen(nextOpen);
            onShow?.(nextOpen);
        };
        const { refs, floatingStyles, context } = useFloating({
            strategy: 'fixed',
            placement,
            open: show || isOpen,
            onOpenChange: setIsOpen,
            middleware: [
                offset(_offset),
                !placement && autoPlacement && autoPlacementMiddleware({ padding: 8 }),
                !autoPlacement && flip(),
                // Clamps the panel back into the viewport when flipping alone cannot keep it
                // there — e.g. an anchor inside a scrolled container whose top has moved off
                // screen. DropdownMenu and ContextMenu already do this; the tooltip was the
                // only floating element without it.
                shift({ padding: 8 }),
                matchReferenceWidth &&
                    floatingSize({
                        padding: 8,
                        apply({ rects, availableWidth, elements }) {
                            // A minimum rather than a fixed width, so a panel that needs more
                            // room than its anchor (a dropdown under a narrow input) can still grow.
                            elements.floating.style.minWidth = `${Math.min(rects.reference.width, availableWidth)}px`;
                        }
                    }),
                withArrow &&
                    arrow({
                        element: arrowRef
                    })
            ]
        });
        const dismiss = useDismiss(context, { ancestorScroll: true });
        const { isMounted, styles } = useTransitionStyles(context);
        const hover = useHover(context, {
            enabled: trigger === 'hover' && !disabled,
            handleClose: reachable ? safePolygon() : undefined
        });
        // keyboard users reach hover tooltips by tabbing; useFocus only opens on
        // focus-visible, so a mouse click does not pop the tooltip open a second time
        const focus = useFocus(context, { enabled: trigger === 'hover' && !disabled });
        const click = useClick(context, { enabled: trigger === 'click' && !disabled });
        const { getReferenceProps, getFloatingProps } = useInteractions([click, hover, focus, dismiss]);

        useImperativeHandle(
            ref,
            () => ({
                show: () => setIsOpen(true),
                hide: () => setIsOpen(false)
            }),
            []
        );

        if (!tooltip) {
            return <>{children}</>;
        }

        return (
            <>
                {!disabled && isMounted && (show || isOpen) && (
                    <FloatingPortal>
                        {trigger === 'manual' ? (
                            <div
                                className={tooltipClasses({
                                    withoutStyling,
                                    flat,
                                    keepClosed: typeof tooltip === 'string',
                                    className: tooltipClassName
                                })}
                                style={{ ...floatingStyles, ...styles, zIndex: 2 ** 31 - 1 }}
                                ref={refs.setFloating}
                                {...getFloatingProps({ onClick: (e) => e.stopPropagation() })}
                            >
                                {withArrow && <FloatingArrow className="tw-fill-white" ref={arrowRef} context={context} />}
                                {tooltip}
                            </div>
                        ) : trigger === 'click' ? (
                            <FloatingFocusManager context={context} modal={false} initialFocus={refs.floating}>
                                <div
                                    className={tooltipClasses({
                                        withoutStyling,
                                        flat,
                                        keepClosed: typeof tooltip === 'string',
                                        className: tooltipClassName
                                    })}
                                    style={{ ...floatingStyles, ...styles, zIndex: 2 ** 31 - 1 }}
                                    ref={refs.setFloating}
                                    {...getFloatingProps({ onClick: (e) => e.stopPropagation() })}
                                >
                                    {withArrow && (
                                        <FloatingArrow className="tw-fill-white" ref={arrowRef} context={context} />
                                    )}
                                    {tooltip}
                                </div>
                            </FloatingFocusManager>
                        ) : (
                            <div
                                className={tooltipClasses({
                                    withoutStyling,
                                    flat,
                                    keepClosed: typeof tooltip === 'string',
                                    className: tooltipClassName
                                })}
                                style={{ ...floatingStyles, ...styles, zIndex: 2 ** 31 - 1 }}
                                ref={refs.setFloating}
                                {...getFloatingProps({ onClick: (e) => e.stopPropagation() })}
                            >
                                {withArrow && (
                                    <FloatingArrow className="tw-fill-white" ref={arrowRef} context={context} />
                                )}
                                {tooltip}
                            </div>
                        )}
                    </FloatingPortal>
                )}
                <div ref={refs.setReference} {...getReferenceProps({ className, ...props })}>
                    {children}
                </div>
            </>
        );
    }
);

Tooltip.displayName = 'Tooltip';

export type { Props as TooltipProps };

export default Tooltip;
